import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Barcode, Camera, X, MagnifyingGlass, GameController, Plus,
  ArrowRight, Check, Spinner, Globe
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

// ════════════════════════════════════════
//  BARCODE SCANNER MODAL
// ════════════════════════════════════════

export function BarcodeScannerModal({ open, onClose, onGameFound }) {
  const [scanning, setScanning] = useState(false);
  const [scannedCode, setScannedCode] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);

  useEffect(() => {
    if (!open) {
      stopScanner();
      setScannedCode('');
      setLookupResult(null);
      setManualCode('');
    }
  }, [open]);

  const startScanner = async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode("barcode-reader");
      html5QrRef.current = scanner;
      setScanning(true);

      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 280, height: 150 }, aspectRatio: 1.777 },
        (decodedText) => {
          handleBarcodeScan(decodedText);
          scanner.stop().catch(() => {});
          setScanning(false);
        },
        () => {}
      );
    } catch (err) {
      toast.error('Camera access denied or unavailable');
      setScanning(false);
    }
  };

  const stopScanner = () => {
    if (html5QrRef.current) {
      html5QrRef.current.stop().catch(() => {});
      html5QrRef.current = null;
    }
    setScanning(false);
  };

  const handleBarcodeScan = async (code) => {
    setScannedCode(code);
    setLoading(true);
    try {
      const { data } = await ax().get(`/api/game-db/barcode/${code}`);
      setLookupResult(data);
      if (data.found) {
        toast.success(`Found: ${data.game_name}`);
      }
    } catch {
      setLookupResult({ found: false, barcode: code });
    }
    setLoading(false);
  };

  const handleManualLookup = () => {
    if (manualCode.trim()) handleBarcodeScan(manualCode.trim());
  };

  const handleAddGame = (gameName, gameId) => {
    onGameFound?.({ game_name: gameName, game_id: gameId, barcode: scannedCode });
    onClose();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}>
        <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          onClick={e => e.stopPropagation()}
          className="bg-[#0e0e16] border border-white/[0.1] rounded-2xl w-full max-w-sm overflow-hidden"
          data-testid="barcode-scanner-modal">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
            <h3 className="text-white font-bold flex items-center gap-2">
              <Barcode size={18} weight="duotone" className="text-[#9D4CDD]" /> Scan Game
            </h3>
            <button onClick={onClose} className="text-[#555] hover:text-white"><X size={18} /></button>
          </div>

          <div className="p-5 space-y-4">
            {/* Camera Scanner */}
            <div id="barcode-reader" ref={scannerRef}
              className={`rounded-xl overflow-hidden bg-black ${scanning ? 'min-h-[200px]' : 'h-0'}`} />

            {!scanning && !scannedCode && (
              <>
                <button onClick={startScanner}
                  className="w-full py-4 rounded-xl bg-[#9D4CDD]/10 border border-[#9D4CDD]/20 text-[#9D4CDD] font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#9D4CDD]/20 transition-colors"
                  data-testid="start-scanner-btn">
                  <Camera size={20} /> Open Camera Scanner
                </button>

                <div className="text-center text-[#444] text-xs">or enter barcode manually</div>

                <div className="flex gap-2">
                  <input className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/40"
                    placeholder="Enter UPC/EAN barcode" value={manualCode} onChange={e => setManualCode(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleManualLookup()}
                    data-testid="manual-barcode-input" />
                  <button onClick={handleManualLookup} disabled={!manualCode.trim()}
                    className="px-4 py-2.5 rounded-lg bg-[#9D4CDD] text-white text-sm font-semibold disabled:opacity-40"
                    data-testid="manual-lookup-btn">
                    <MagnifyingGlass size={16} />
                  </button>
                </div>
              </>
            )}

            {/* Loading */}
            {loading && (
              <div className="text-center py-6">
                <div className="w-8 h-8 border-3 border-[#9D4CDD] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-[#888] text-sm">Looking up barcode...</p>
              </div>
            )}

            {/* Result */}
            {!loading && scannedCode && lookupResult && (
              <div>
                <div className="bg-white/[0.03] rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
                  <Barcode size={14} className="text-[#555]" />
                  <span className="text-[#888] text-xs font-mono">{scannedCode}</span>
                </div>

                {lookupResult.found ? (
                  <div className="bg-[#10B981]/[0.06] border border-[#10B981]/20 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                        <GameController size={20} weight="duotone" className="text-[#10B981]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold text-sm">{lookupResult.game_name}</p>
                        <p className="text-[#10B981] text-[10px]">Match found!</p>
                      </div>
                    </div>
                    <button onClick={() => handleAddGame(lookupResult.game_name, lookupResult.game_id)}
                      className="w-full mt-3 py-2.5 rounded-lg bg-[#10B981] text-white text-sm font-semibold"
                      data-testid="add-scanned-game">
                      <Plus size={14} className="inline mr-1" /> Add to Collection
                    </button>
                  </div>
                ) : (
                  <div className="bg-[#F59E0B]/[0.06] border border-[#F59E0B]/20 rounded-xl p-4">
                    <p className="text-white text-sm font-medium mb-1">Game not found for this barcode</p>
                    <p className="text-[#888] text-xs mb-3">Search for the game by name to map this barcode.</p>
                    <BGGSearchInline barcode={scannedCode} onSelect={handleAddGame} />
                  </div>
                )}

                <button onClick={() => { setScannedCode(''); setLookupResult(null); }}
                  className="w-full mt-3 py-2 text-center text-[#9D4CDD] text-xs hover:underline">
                  Scan Another
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}


// ── BGG Search Inline (for unmapped barcodes) ──

function BGGSearchInline({ barcode, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await ax().get(`/api/game-db/bgg/search?q=${encodeURIComponent(query)}`);
      setResults(data || []);
    } catch { toast.error('Search failed'); }
    setSearching(false);
  };

  const selectGame = async (game) => {
    // Map barcode to this game
    try {
      await ax().post('/api/game-db/barcode/map', {
        barcode, game_name: game.name, bgg_id: game.bgg_id || undefined,
      });
    } catch {}

    // If it has a game_id from local DB, use that. Otherwise import from BGG or use name.
    if (game.game_id) {
      onSelect(game.name, game.game_id);
    } else if (game.bgg_id) {
      try {
        const { data } = await ax().post(`/api/game-db/bgg/import/${game.bgg_id}`);
        onSelect(game.name, data.game_id);
      } catch {
        onSelect(game.name, null);
      }
    } else {
      onSelect(game.name, null);
    }
  };

  return (
    <div data-testid="bgg-search-inline">
      <div className="flex gap-2 mb-2">
        <input className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-xs placeholder:text-[#444] focus:outline-none"
          placeholder="Search game name..." value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()} data-testid="bgg-search-input" />
        <button onClick={search} disabled={searching || !query.trim()}
          className="px-3 py-2 rounded-lg bg-[#F59E0B] text-black text-xs font-semibold disabled:opacity-40">
          Search
        </button>
      </div>
      {results.length > 0 && (
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {results.map((g, i) => (
            <button key={`${g.bgg_id || g.game_id}-${i}`} onClick={() => selectGame(g)}
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-white/[0.04] text-left transition-colors">
              <Globe size={12} className="text-[#F59E0B] shrink-0" />
              <span className="text-white text-xs flex-1 truncate">{g.name}</span>
              {g.year_published && <span className="text-[#555] text-[10px]">{g.year_published}</span>}
              <span className="text-[8px] text-[#444]">{g.source || 'bgg'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
